import React from 'react'
import InfoTooltip from "../../components/InfoTooltip";
import EmptyBanner from "../../components/EmptyBanner";
import Project from "../../models/Project";
import {useNavigate, useOutletContext, Link} from "react-router-dom";
import {useAuth} from "../../providers/AuthProvider";

const ProjectGroupsList = (props) => {

    const {auth} = useAuth();
    const {
        groups,
        publicGroup,
        disabled,
        onEditGroup,
        onDeleteGroup
    } = useOutletContext();

    const canManage = auth.isClientAdmin() || auth.canManageGroups();

    return (
        <div className="container manage-project-group-container xl:max-w-6xl">

            <p className="pt-6 pb-2 text-base"><strong>Groups</strong> can be used within the Wripple platform to
                manage workflow, project visibility and user permissions across your
                company. {auth.isClientSuperAdmin() && (
                    <span>As a super admin, you are automatically included in all groups created, with full
                        access.</span>
                )} Only Super Admins can create groups and assign permissions.</p>

            <div className="mt-4 flex items-stretch flex-col">

                {publicGroup && (
                    <div className="mb-4">
                        <GroupTable
                            groups={[publicGroup]}
                            disabled={disabled}
                            canManage={false}
                            isPublic={true}
                        />
                    </div>
                )}
                {groups.length > 0 ? (
                    <GroupTable
                        groups={groups}
                        disabled={disabled}
                        canManage={canManage}
                        onEditGroup={onEditGroup}
                        onDeleteGroup={onDeleteGroup}/>
                ) : (
                    <EmptyBanner message="No Groups Created"/>
                )}
            </div>
        </div>
    )
}
const GroupUsersList = ({users, usersCountToShow = 12}) => {
    const formattedUsers = users.map(user => user.firstNamePlusInitial().slice(0, -1));
    const usersToShow = formattedUsers.slice(0, usersCountToShow);
    const slicedUsers = formattedUsers.slice(usersCountToShow);

    return (
        <p>
            {usersToShow.join(', ')}
            {!!slicedUsers.length && (
                <React.Fragment>
                    <span id="public-more-members" className="text-underline cursor-none text-primary">
                        {`...plus ${slicedUsers.length} more`}
                    </span>
                    <InfoTooltip
                        type="dark"
                        target="public-more-members">
                        {slicedUsers.join(', ')}
                    </InfoTooltip>
                </React.Fragment>
            )}
        </p>
    );
}
const GroupTable = ({groups, canManage = false, isPublic = false, disabled, ...props}) => {
    const {auth} = useAuth()
    const canManageGroups = auth?.canManageGroups();
    const navigate = useNavigate()

    const goToGroup = async (group) => {
        return navigate(`/client/org/groups/${group.id}`);
    }

    return (
        <div className="bg-white p-2 md:p-4">
            <p className="text-base pb-3">
                {isPublic ?
                    <span>By Default, all Wripple users are included in the <strong>Public Group</strong>, where
                        they have full access</span> : 'Custom Groups'}
            </p>
            <table className="table-auto w-full">
                <thead className="border-b border-gray-300">
                <tr className="text-left">
                    <th className="whitespace-nowrap p-2 md:p-4">Group name</th>
                    <th className="hidden sm:table-cell p-2 md:p-4">Admins</th>
                    <th className="p-2 md:p-4">Members</th>
                    {canManageGroups && <th>Projects</th>}

                    {canManage && <th/>}
                </tr>
                </thead>

                <tbody>
                    {groups?.length > 0 && groups.map((group, index) => {
                        const [allAdmins, allMembers] = group.separateAdminsAndMembers();
                        let key = TB.slugify(group.name + index);
                        
                        return (
                            <tr key={key} className="odd:bg-light-gray border-t border-gray-300 text-xs">
                                <td className="p-2 md:p-4">
                                    {group.id ? (
                                        <button type="button" className="text-left text-underline text-info p-2" onClick={() => goToGroup(group)}>
                                            {group.name}
                                        </button>
                                    ) : (
                                        <p className="text-dark p-2">{group.name}</p>
                                    )}
                                </td>

                                <td className="sm:hidden p-2 md:p-4">
                                    <p>{group.members.length}</p>
                                </td>
                                <td className="hidden p-2 sm:table-cell md:p-4">
                                    <GroupUsersList users={allAdmins}/>
                                </td>
                                <td className="hidden p-2 sm:table-cell md:p-4">
                                    <GroupUsersList users={allMembers}/>
                                </td>
                                {canManageGroups && (
                                    <td style={{width: 20 + '%'}}>
                                        <GroupProjectsDisplay group={group}/>
                                    </td>
                                )}
                                {canManage && (
                                    <td className="p-2 md:p-4">
                                        <div className="flex flex-nowrap">
                                            <div className="mr-2">
                                                <button className="btn btn-dark bg-zinc-700 p-2 table-btn"
                                                        id={`manage-group-btn-${key}`}
                                                        onClick={() => goToGroup(group)}
                                                        disabled={disabled}>
                                                    <i className="fas fa-users text-white"/>
                                                </button>
                                                <InfoTooltip
                                                    type="dark"
                                                    target={`manage-group-btn-${key}`}>
                                                    Manage Permissions
                                                </InfoTooltip>
                                            </div>
                                            <div>
                                                <button className="btn btn-info p-2 table-btn"
                                                        id={`edit-group-btn-${key}`}
                                                        onClick={() => props.onEditGroup(group)}
                                                        disabled={disabled}>
                                                    <i className="fas fa-edit text-white"/>
                                                </button>
                                                <InfoTooltip
                                                    type="info"
                                                    target={`edit-group-btn-${key}`}>
                                                    Edit Group Details
                                                </InfoTooltip>
                                            </div>
                                            {auth?.canManageGroups() && (
                                                <div className="mx-2">
                                                    <button className="btn bg-danger p-2 table-btn"
                                                            id={`delete-group-btn-${key}`}
                                                            onClick={() => props.onDeleteGroup(group)}
                                                            disabled={disabled}>
                                                        <i className="fas fa-trash-alt text-white"/>
                                                    </button>
                                                    <InfoTooltip
                                                        type="danger"
                                                        target={`delete-group-btn-${key}`}>
                                                        Delete Group
                                                    </InfoTooltip>
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                )}
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>

    )
}

const GroupProjectsDisplay = ({group}) => {

    if (!group.projects.length) {
        return <i>N/A</i>
    }
    return (
        <div className="flex flex-row flex-wrap justify-start items-center">
            <button onClick={() => TB.event("Modal@show", {
                title: (<span>Projects assigned to <b>{group.name}</b></span>),
                modalClass: "group-projects-detail-modal",
                content: (
                    <table className="table table-bordered">
                        <thead className="thead-light">
                        <tr>
                            <th><p className="mb-0"><small>Project</small></p></th>
                            <th><p className="mb-0"><small>Status</small></p></th>
                            <th><p className="mb-0"><small>Created</small></p></th>
                            <th><p className="mb-0"><small>Action</small></p></th>
                        </tr>
                        </thead>
                        <tbody>
                        {group.projects.map((project, i) => (
                            <tr key={`group-${group.id}-p-${project.id}-${i}`}>
                                <td><p className="mb-0"><small>{project.name}</small></p></td>
                                <td><p className="status-badge"
                                       style={{background: Project.colorForStatus(project.status)}}>
                                    <small>{project.status}</small>
                                </p></td>
                                <td><p className="mb-0"><small>{TB.moment(project.created_at).format('ll')}</small></p>
                                </td>
                                <td>
                                    <Link className="whitespace-no-wrap mx-2 btn btn-link btn-sm btn-link-primary"
                                          to={project.detailLink()} target="_blank">
                                        <small>Open</small>
                                    </Link>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                )
            })} className="btn btn-link btn-link-primary">
                {TB.pluralize("Project", group.projects.length, true)}
            </button>
        </div>
    )
}

export default ProjectGroupsList;